/* eslint-disable i18next/no-literal-string */
import { cn } from "~/lib/utils";
import { colors } from "~/components/landing/colors";

/**
 * Ported from bouncieweb's `components/PrivacyPromised.js`.
 *
 * Faithful reproduction of the Emotion-styled privacy callout: a rounded
 * (borderRadius 20) card on a blueLight8 (#E1EDFD) background, split into a dark
 * blue (blueDark6 #0C2A61) left panel with the "Privacy. Promised." headline and
 * a right panel with the protected-data blurb + a "US Based" block whose flag
 * motif is three vertical stripes (red / white / navy).
 *
 * Original layout: column on mobile (card width 275px), full width from xsm
 * (431px), and row layout (centered, no auto margins) from md (768px).
 *
 * Breakpoint map: xsm 431 -> min-[431px]:  md 768 -> md:  lg 1024 -> lg:
 */

// Stripe: a single vertical color band of the flag motif.
function Stripe({ color, width }: { color: string; width: number }) {
  return (
    <span
      className="inline-block h-full"
      style={{ background: color, width }}
    />
  );
}

export default function PrivacyPromised({
  className,
}: {
  className?: string;
} = {}) {
  return (
    // PrivacySection — `className` stands in for the original `styles` css prop.
    <div
      className={cn(
        "mx-auto my-8 flex flex-col justify-between overflow-hidden rounded-[20px] text-left text-[18px]",
        "w-[275px]",
        "min-[431px]:w-full",
        "md:mx-0 md:flex-row md:items-center md:text-left",
        className,
      )}
      style={{ background: colors.blueLight8 }}
    >
      {/* Left */}
      <div
        className={cn(
          "w-full px-[26px] pt-[14px] pr-[32px] pb-[13px] text-white",
          "md:px-12 md:py-[6.25rem] md:basis-[60%]",
          "lg:px-12 lg:py-[5.25rem] lg:max-w-none lg:basis-[65%]",
        )}
        style={{ background: colors.blueDark6 }}
      >
        {/* LandingPageHeaderCustom */}
        <h2 className="m-0 mb-1 text-left text-[32px] leading-[1.2] font-bold md:text-[35px] lg:text-[52px]">
          Privacy. Promised.
        </h2>
        {/* CustomP with textWrap nowrap */}
        <p className="mt-0 mb-2 text-[18px] leading-[1.2] [text-wrap:nowrap] lg:text-[24px]">
          Never sold. Never shared.
        </p>
      </div>

      {/* Right */}
      <div
        className={cn(
          "pt-[15px] pr-[40px] pb-[20px] pl-[26px]",
          "min-[431px]:flex min-[431px]:justify-start min-[431px]:gap-4",
          "md:block md:grow md:p-0 md:pl-8",
        )}
      >
        {/* Explanation */}
        <div className="mb-8 w-[13rem] font-bold">
          Driving, location,
          <br />
          and vehicle data is
          <br />
          always protected.
        </div>
        <div>
          {/* CustomH3 */}
          <h3 className="mt-0 mb-2 text-[20px] leading-[1.2] font-bold lg:text-[24px]">
            US Based
          </h3>
          {/* USTable */}
          <div className="inline-flex gap-2 text-left text-[16px] leading-[1.4]">
            <div className="w-[17.5px]">
              <Stripe color="#BB133E" width={7} />
              <Stripe color="#FFF" width={3.5} />
              <Stripe color="#002147" width={7} />
            </div>
            <div>
              <div>Company</div>
              <div>Data Centers</div>
              <div>Manufacturing</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
